import React, { useCallback } from 'react';
import { TextV1 } from '@withjoy/joykit';
import type { ExtendedOverlayableProps } from '@withjoy/joykit';
import { DialogV1, useOverlayUnstable } from '@withjoy/joykit';
import type { EventPickerProps, UseEventPickerControllerArgs } from './EventPicker.controller';
import { useEventPickerController } from './EventPicker.controller';
import { EventPicker } from './EventPicker';
import { StyledDialog } from './EventPicker.styles';
import type { EventMembership } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';

const OVERLAY_KEY = 'event-picker';

const EventPickerDialogContent: React.FC<EventPickerProps & ExtendedOverlayableProps> = props => {
  const { overlayKey, hide, handleOnEventClick } = props;
  const { tq } = useTranslation('account');

  const onClose = useCallback(() => {
    hide(overlayKey);
  }, [hide, overlayKey]);

  const onEventClickAndHide = useCallback<typeof handleOnEventClick>(
    (...args) => {
      handleOnEventClick(...args);
      onClose();
    },
    [handleOnEventClick, onClose],
  );

  return (
    <StyledDialog
      overlayKey={overlayKey}
      canCloseOnOutsideClick={true}
      canCloseOnEscapeKey={true}
    >
      <DialogV1.Header
        title={
          <TextV1
            textAlign="left"
            lineHeight="32px"
            letterSpacing="-0.24px"
            fontWeight={600}
            fontStyle="normal"
            fontSize="24px"
            color={'black'}
            component="h1"
            marginY={6}
            marginX={7}
          >
            {tq`Select an Event`}
          </TextV1>
        }
        onClose={onClose}
      ></DialogV1.Header>
      <DialogV1.Body>
        <EventPicker
          {...props}
          handleOnEventClick={onEventClickAndHide}
        />
      </DialogV1.Body>
    </StyledDialog>
  );
};

export type UseEventPickerDialogArgs = {
  onOpen?: (memberships: EventMembership[]) => void;
} & UseEventPickerControllerArgs &
  Pick<EventPickerProps, 'currentEventId' | 'onCreateNewEvent'>;

export const useEventPickerDialog = ({ onEventClick, onOpen, membershipStatus, currentEventId, onCreateNewEvent }: UseEventPickerDialogArgs) => {
  const { handleOnEventClick, eventMemberships, cashOnlyEventIds } = useEventPickerController({
    onEventClick,
    membershipStatus,
  });
  const [showEventPickerDialog, hideEventPickerDialog] = useOverlayUnstable<EventPickerProps>(
    {
      overlayKey: OVERLAY_KEY,
      render: props => <EventPickerDialogContent {...props} />,
      props: {
        handleOnEventClick,
        eventMemberships,
        currentEventId,
        onCreateNewEvent,
        cashOnlyEventIds,
      },
    },
    {
      dependencies: [currentEventId, eventMemberships, handleOnEventClick, onCreateNewEvent, cashOnlyEventIds],
    },
  );

  const wrappedShowEventPicker = useEventCallback(() => {
    onOpen?.(eventMemberships);
    showEventPickerDialog();
  });

  return [wrappedShowEventPicker, hideEventPickerDialog];
};
